import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSocket } from "../context/SocketContext";
import IdleScreenTimeModal from "../modal/IdleScreenTimeModal";
import {
  clearTicketDetailFromStorage,
  getPaymentSubscriptionUserId,
} from "../utils/ticket";
import {
  DEFAULT_KIOSK_TIMEOUT_MS,
  fetchKioskTimeoutSettings,
} from "../services/kioskTimeoutService";

/** Idle warning is only for in-session pages (not attract / QR). */
const EXCLUDED_IDLE_PATHS = ["/", "/qr-scan"] as const;

/** Avoid mousemove — it never lets the idle timer finish on kiosk/desktop. */
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "pointerdown",
  "click",
  "scroll",
] as const;

const ACTIVITY_THROTTLE_MS = 500;

const shouldTrackIdle = (pathname: string) =>
  !EXCLUDED_IDLE_PATHS.includes(
    pathname as (typeof EXCLUDED_IDLE_PATHS)[number]
  );

const KioskSessionManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentSocket, paymentConnected } = useSocket();
  const idleWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionExpireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(DEFAULT_KIOSK_TIMEOUT_MS);
  const [logoutSessionMs, setLogoutSessionMs] = useState(DEFAULT_KIOSK_TIMEOUT_MS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const showIdleModalRef = useRef(false);
  const idleTimeoutMsRef = useRef(idleTimeoutMs);
  const logoutSessionMsRef = useRef(logoutSessionMs);
  const settingsLoadedRef = useRef(false);
  const lastActivityAtRef = useRef(0);
  const pathnameRef = useRef(location.pathname);

  showIdleModalRef.current = showIdleModal;
  idleTimeoutMsRef.current = idleTimeoutMs;
  logoutSessionMsRef.current = logoutSessionMs;
  settingsLoadedRef.current = settingsLoaded;
  pathnameRef.current = location.pathname;

  useEffect(() => {
    let cancelled = false;
    fetchKioskTimeoutSettings().then((settings) => {
      if (cancelled) return;
      const warningMs =
        settings.milliseconds > 0
          ? settings.milliseconds
          : DEFAULT_KIOSK_TIMEOUT_MS;
      const graceMs =
        settings.logoutSessionMs > 0
          ? settings.logoutSessionMs
          : DEFAULT_KIOSK_TIMEOUT_MS;
      setIdleTimeoutMs(warningMs);
      setLogoutSessionMs(graceMs);
      setSettingsLoaded(true);
      // eslint-disable-next-line no-console
      console.info(
        `[KioskSession] idle warning=${warningMs}ms, logout grace=${graceMs}ms`
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToIdle = useCallback(() => {
    showIdleModalRef.current = false;
    setShowIdleModal(false);
    clearTicketDetailFromStorage();
    try {
      localStorage.removeItem("selectedCan");
    } catch {
      /* ignore */
    }
    navigate("/", { replace: true });
  }, [navigate]);

  const clearIdleTimers = useCallback(() => {
    if (idleWarningTimerRef.current) {
      clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
      sessionExpireTimerRef.current = null;
    }
  }, []);

  const startSessionExpireTimer = useCallback(() => {
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
    }

    // After warning: use API logout_session (not another full idle period)
    sessionExpireTimerRef.current = setTimeout(() => {
      goToIdle();
    }, logoutSessionMsRef.current);
  }, [goToIdle]);

  const showIdleWarning = useCallback(() => {
    // Sync ref immediately so activity handlers do not dismiss the modal
    showIdleModalRef.current = true;
    setShowIdleModal(true);
    startSessionExpireTimer();
  }, [startSessionExpireTimer]);

  const resetInactivityTimer = useCallback(() => {
    clearIdleTimers();
    showIdleModalRef.current = false;
    setShowIdleModal(false);

    if (!settingsLoadedRef.current || !shouldTrackIdle(pathnameRef.current)) {
      return;
    }

    idleWarningTimerRef.current = setTimeout(() => {
      showIdleWarning();
    }, idleTimeoutMsRef.current);
  }, [clearIdleTimers, showIdleWarning]);

  const handleIdleContinue = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleIdleQuit = useCallback(() => {
    clearIdleTimers();
    goToIdle();
  }, [clearIdleTimers, goToIdle]);

  useEffect(() => {
    if (!settingsLoaded || !shouldTrackIdle(location.pathname)) {
      clearIdleTimers();
      showIdleModalRef.current = false;
      setShowIdleModal(false);
      return;
    }

    resetInactivityTimer();

    const onActivity = () => {
      if (showIdleModalRef.current) return;

      const now = Date.now();
      if (now - lastActivityAtRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityAtRef.current = now;

      resetInactivityTimer();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true })
    );

    return () => {
      clearIdleTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity)
      );
    };
  }, [
    clearIdleTimers,
    idleTimeoutMs,
    logoutSessionMs,
    location.pathname,
    resetInactivityTimer,
    settingsLoaded,
  ]);

  useEffect(() => {
    if (!paymentSocket || !paymentConnected) return;

    const userId = getPaymentSubscriptionUserId();
    if (!userId) return;

    paymentSocket.emit("subscribeToPaymentStatus", { userId });

    const onPaymentStatusUpdate = (payload: { type?: string }) => {
      if (payload?.type === "extend") {
        if (!showIdleModalRef.current) resetInactivityTimer();
      }
    };

    paymentSocket.on("paymentStatusUpdate", onPaymentStatusUpdate);

    return () => {
      paymentSocket.off("paymentStatusUpdate", onPaymentStatusUpdate);
    };
  }, [paymentSocket, paymentConnected, resetInactivityTimer, location.pathname]);

  return (
    <IdleScreenTimeModal
      show={showIdleModal}
      onHide={handleIdleContinue}
      onContinue={handleIdleContinue}
      onQuit={handleIdleQuit}
    />
  );
};

export default KioskSessionManager;
